/**
 * 홈 페이지 v5.0 — 네이버 스타일 포털.
 *
 * v4.0(케어닥 스타일) → v5.0(네이버 스타일) 전환:
 *  - 좌:우 = 8:4 고정, 우측 sticky sidebar에 MyWidget + TodayInsight + 광고 + 정부자원
 *  - 좌측 row 1: HotRanking + NewsHeadlineCard
 *  - 광고 미등록 시 MockPromotionFallback 자동 노출
 *  - 모바일: TodayInsight·HotRanking·NewsHeadline 추가 노출
 *
 * 영구지침: docs/develop/frontend/home-page-portal-spec.md
 *
 * 롤백: HomePage.legacy.tsx 가 v3.0 사본. v4.0 백업은 git 히스토리.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Search,
  Building2,
  Sparkles,
  ClipboardCheck,
  Calculator,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Heart,
  BookOpen,
  MapPin,
  HeartHandshake,
} from 'lucide-react'
import apiClient from '@/lib/api/client'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useHasMounted } from '@/hooks/useHasMounted'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/lib/constants'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import { GOVERNMENT_RESOURCES } from '@/data/governmentResources'

import SEO from '@/components/common/SEO'
import { OrganizationJsonLd, WebSiteJsonLd } from '@/components/common/JsonLd'
import PromotionSlot from '@/components/common/PromotionSlot'
import StickySideBanner from '@/components/common/StickySideBanner'
import MockPromotionFallback from '@/components/common/MockPromotionFallback'
import StatisticsSection from '@/components/home/StatisticsSection'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Skeleton from '@/components/ui/Skeleton'

import MyWidget from '@/components/home/portal/MyWidget'
import HotRankingWidget from '@/components/home/portal/HotRankingWidget'
import TodayInsightCard from '@/components/home/portal/TodayInsightCard'
import NewsHeadlineCard from '@/components/home/portal/NewsHeadlineCard'
import PopularFacilities from '@/components/home/portal/PopularFacilities'
import CommunityHotPosts from '@/components/home/portal/CommunityHotPosts'
import BlogHeadlines from '@/components/home/portal/BlogHeadlines'
import ShopRecommend from '@/components/home/portal/ShopRecommend'
import PwaInstallCard from '@/components/home/portal/PwaInstallCard'
import ChecklistPromoCard from '@/components/checklist/ChecklistPromoCard'

import { useQuery } from '@tanstack/react-query'
import { mobilePopularQueryKey } from '@/features/home/widgetQueries'

export default function HomePage() {
  const isDesktop = useIsDesktop()
  return (
    <>
      <SEO />
      <OrganizationJsonLd />
      <WebSiteJsonLd />
      {isDesktop ? <DesktopPortalHome /> : <MobilePortalHome />}
    </>
  )
}

/* ============================================================================
 * Desktop — 네이버 스타일 12-column 포털
 * ========================================================================== */

function DesktopPortalHome() {
  return (
    <>
      {/* 좌우 세로배너 160x600 — 콘텐츠 max-w-6xl 이라 2xl 초광폭에서만.
          히어로(-mt-16 pt-24 pb-16, 약 309px) 아래로 노출, 푸터 위에서 멈춤(StickySideBanner) */}
      <StickySideBanner slotKey="home_side_left" side="left" topPx={384} />
      <StickySideBanner slotKey="home_side_right" side="right" topPx={384} />
    <main className="flex-1 bg-cb-gray-50 pb-16">
      {/* ── Hero (compact) ─────────────────────────────────────────────── */}
      <CompactHero />

      {/* ── 시설 유형 칩 8개 (검색 아래 고정 진입로) ──────────────────── */}
      <FacilityTypeChips />

      {/* ── 좌측 콘텐츠(8) + 우측 sticky sidebar(4) ───────────────────── */}
      <section className="mx-auto mt-6 max-w-6xl px-6">
        <div className="grid grid-cols-12 items-start gap-6">
          {/* ── Left main column ─────────────────────────────────────── */}
          <div className="col-span-8 flex flex-col gap-6">
            {/* 메인 상단 배너 (네이버 히어로 패턴: 새로고침마다 가중 랜덤 1개) */}
            <PromotionSlot
              slotKey="main_card_slider"
              limit={1}
              layout="single"
              fallback={<MockPromotionFallback variant="hero" />}
            />

            {/* 요양 가이드 + 비용계산 — 고유 편집 콘텐츠(가이드)를 상단으로 올려 심사 동선·정보 가치 강화 */}
            <div className="grid grid-cols-2 gap-4">
              <GuideWidget />
              <CostSimulatorPromo />
            </div>

            {/* row 1: HotRanking + NewsHeadline */}
            <div className="grid grid-cols-2 gap-4">
              <HotRankingWidget />
              <NewsHeadlineCard />
            </div>

            {/* 체크리스트 진입 CTA — 광고 직후 연속 배너 인상을 피하기 위해 뉴스 다음으로 배치 */}
            <ChecklistPromoCard variant="home" />

            {/* row 3: 커뮤니티 풀폭 */}
            <CommunityHotPosts />
          </div>

          {/* ── Right sticky sidebar ─────────────────────────────────── */}
          <div className="col-span-4">
            <aside className="sticky top-4 flex flex-col gap-6">
              <MyWidget />
              <PromotionSlot
                slotKey="main_my_below"
                limit={1}
                layout="single"
                fallback={<MockPromotionFallback variant="hero" />}
              />
              <TodayInsightCard />
              <PromotionSlot
                slotKey="main_side_ad_grid"
                limit={4}
                layout="grid"
                fallback={<MockPromotionFallback variant="grid" count={4} />}
              />
              <GovResourcesWidget />
            </aside>
          </div>
        </div>
      </section>

      {/* ── 풀폭 광고 main_hero ────────────────────────────────────────── */}
      <section className="mx-auto mt-6 max-w-6xl px-6">
        <PromotionSlot
          slotKey="main_hero"
          fallback={<MockPromotionFallback variant="hero" />}
        />
      </section>

      {/* ── 통계 (풀폭) ────────────────────────────────────────────────── */}
      <div className="mt-2">
        <StatisticsSection />
      </div>

      {/* ── 라이프 콘텐츠 4-col ───────────────────────────────────────── */}
      <section className="mx-auto mt-2 max-w-6xl px-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-3">
            <BlogHeadlines />
          </div>
          <div className="col-span-3">
            <PopularFacilities />
          </div>
          <div className="col-span-3">
            <CareLogPromo />
          </div>
          <div className="col-span-3">
            <ShopRecommend />
          </div>
        </div>
      </section>

      {/* ── PWA 설치 카드 ─────────────────────────────────────────────── */}
      <section className="mx-auto mt-6 max-w-6xl px-6">
        <PwaInstallCard />
      </section>

      {/* ── 마지막 CTA ────────────────────────────────────────────────── */}
      <section className="mx-auto mt-6 max-w-6xl px-6">
        <div className="rounded-2xl bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] px-8 py-8 text-center">
          <h2 className="text-xl font-bold text-white">
            지금 우리 동네 시설을 찾아보세요
          </h2>
          <p className="mt-1 text-sm text-white/70">
            32,580개 시설 · 무료 상담 · AI 맞춤 추천
          </p>
          <Link
            to={ROUTES.FACILITIES}
            className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-white px-7 py-3 text-sm font-bold text-[#2563eb] hover:bg-cb-gray-50"
          >
            시설 검색하기 <ArrowRight size={16} />
          </Link>
        </div>
      </section>

    </main>
    </>
  )
}

/* -------------------------------------------------------------------------- */

const FACILITY_CHIPS: { type: string; label: string; emoji: string }[] = [
  { type: 'NURSING_HOME', label: '요양원', emoji: '🏥' },
  { type: 'NURSING_HOSPITAL', label: '요양병원', emoji: '⚕️' },
  { type: 'DAY_CARE', label: '주간보호', emoji: '☀️' },
  { type: 'HOME_VISIT_CARE', label: '방문요양', emoji: '🚪' },
  { type: 'HOME_VISIT_BATH', label: '방문목욕', emoji: '🛁' },
  { type: 'SHORT_STAY', label: '단기보호', emoji: '🛏️' },
  { type: 'DEMENTIA_CARE', label: '치매전담', emoji: '🧠' },
  { type: 'HOME_CARE_CENTER', label: '재가복지', emoji: '🏡' },
]

function FacilityTypeChips() {
  const scrollRef = useRef<HTMLUListElement>(null)
  const [overflow, setOverflow] = useState(false)

  useEffect(() => {
    const check = () => {
      const el = scrollRef.current
      if (!el) return
      setOverflow(el.scrollWidth > el.clientWidth + 4)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const scrollByPage = (dir: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const delta = el.clientWidth * 0.8
    el.scrollBy({ left: dir === 'right' ? delta : -delta, behavior: 'smooth' })
  }

  return (
    <section className="border-b border-cb-gray-100 bg-white">
      <div className="group relative mx-auto max-w-6xl px-6 py-4">
        <ul
          ref={scrollRef}
          className="scrollbar-hide flex items-center justify-between gap-2 overflow-x-auto"
        >
          {FACILITY_CHIPS.map((chip) => (
            <li key={chip.type} className="flex-shrink-0">
              <Link
                to={`/facilities?facilityType=${chip.type}`}
                className="flex flex-col items-center gap-1 rounded-xl px-3 py-2 hover:bg-cb-gray-50"
              >
                <span className="text-xl leading-none">{chip.emoji}</span>
                <span className="text-xs font-semibold text-cb-gray-700">
                  {chip.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {overflow && (
          <>
            <button
              type="button"
              onClick={() => scrollByPage('left')}
              aria-label="이전 시설 유형"
              className="absolute left-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-cb-gray-700 shadow-md opacity-0 transition-opacity duration-200 hover:bg-cb-gray-50 group-hover:opacity-95"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => scrollByPage('right')}
              aria-label="다음 시설 유형"
              className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-cb-gray-700 shadow-md opacity-0 transition-opacity duration-200 hover:bg-cb-gray-50 group-hover:opacity-95"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */

function CompactHero() {
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [items, setItems] = useState<
    { id: number; name: string; facilityType: string; addressSido: string }[]
  >([])
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const submit = () => {
    setOpen(false)
    navigate(
      keyword.trim()
        ? `/facilities?keyword=${encodeURIComponent(keyword.trim())}`
        : '/facilities'
    )
  }

  const onChange = (val: string) => {
    setKeyword(val)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (val.trim().length >= 2) {
      timerRef.current = setTimeout(async () => {
        try {
          const res = await apiClient.get('/facilities/autocomplete', {
            params: { query: val.trim() },
          })
          setItems(res.data.data ?? [])
          setOpen(true)
        } catch {
          setItems([])
        }
      }, 300)
    } else {
      setItems([])
      setOpen(false)
    }
  }

  const CHIPS = [
    { icon: Sparkles, label: 'AI 맞춤 추천', to: ROUTES.RECOMMEND },
    { icon: ClipboardCheck, label: '등급 자가진단', to: ROUTES.SELF_ASSESSMENT },
    { icon: Calculator, label: '비용 계산', to: ROUTES.COST_SIMULATOR },
    { icon: Building2, label: '시설 비교', to: '/compare' },
  ]

  return (
    <section className="relative -mt-16 bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#4338ca] pb-16 pt-24">
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <h1 className="text-3xl font-bold leading-tight text-white">
          요양원·요양시설,
          <br />
          <span className="text-blue-200">비용·평가등급까지 비교하세요</span>
        </h1>

        <div className="relative mx-auto mt-6 flex max-w-2xl gap-2">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-cb-gray-400"
            />
            <input
              type="text"
              value={keyword}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              onFocus={() => items.length > 0 && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder="시설 이름·지역명으로 검색"
              className="w-full rounded-xl border-0 bg-white/95 py-3.5 pl-11 pr-4 text-sm shadow-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-white/40"
            />
            {open && items.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-cb-gray-100 bg-white shadow-2xl">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onMouseDown={() => {
                      setOpen(false)
                      navigate(`/facilities/${item.id}`)
                    }}
                    className="flex w-full items-center gap-3 border-b border-cb-gray-50 px-4 py-2.5 text-left last:border-0 hover:bg-cb-primary-light"
                  >
                    <Building2 size={14} className="flex-shrink-0 text-cb-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-cb-gray-900">
                        {item.name}
                      </p>
                      <p className="text-xs text-cb-gray-500">
                        {FACILITY_TYPE_LABELS[item.facilityType] ?? item.facilityType} ·{' '}
                        {item.addressSido}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={submit}
            className="flex-shrink-0 rounded-xl bg-white px-6 text-sm font-bold text-[#2563eb] shadow-lg hover:bg-cb-gray-50"
          >
            검색
          </button>
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {CHIPS.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-white/25"
            >
              <c.icon size={12} />
              {c.label}
            </Link>
          ))}
        </div>

        <p className="mt-3 text-xs text-white/50">
          32,580개 시설 · 평가등급 A~E · AI 맞춤 추천 · 무료 상담
        </p>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */

function GuideWidget() {
  const items = [
    { icon: ClipboardCheck, label: '등급 자가진단', to: ROUTES.SELF_ASSESSMENT, badge: '3분' },
    { icon: BookOpen, label: '요양등급이란?', to: '/guide/care-grade' },
    { icon: Building2, label: '시설 종류 비교', to: '/guide/facility-types' },
    { icon: Calculator, label: '비용 가이드', to: '/guide/cost' },
  ]
  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <BookOpen size={16} className="text-cb-primary" />
          요양 가이드
        </h2>
      </header>
      <ul className="flex flex-col gap-1.5">
        {items.map((item) => (
          <li key={item.label}>
            <Link
              to={item.to}
              className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-cb-gray-50"
            >
              <item.icon size={14} className="text-cb-gray-500" />
              <span className="text-sm font-medium text-cb-gray-900">
                {item.label}
              </span>
              {item.badge && (
                <span className="ml-auto rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                  {item.badge}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function CostSimulatorPromo() {
  return (
    <section className="flex h-full flex-col rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <Calculator size={16} className="text-amber-500" />
          비용 시뮬레이터
        </h2>
      </header>
      <p className="text-sm leading-relaxed text-cb-gray-600">
        요양원·주간보호·방문요양 월 부담금을
        <br />
        1분 만에 계산해 보세요.
      </p>
      <ul className="mt-3 space-y-1.5 text-xs text-cb-gray-500">
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-amber-400" /> 등급별 본인부담금
        </li>
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-amber-400" /> 감경(면제·60%·40%) 반영
        </li>
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-amber-400" /> 식재료비 등 비급여 포함
        </li>
      </ul>
      <Link
        to={ROUTES.COST_SIMULATOR}
        className="mt-auto flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-2.5 text-sm font-bold text-white hover:opacity-90"
      >
        지금 계산하기 <ArrowRight size={14} />
      </Link>
    </section>
  )
}

function CareLogPromo() {
  return (
    <section className="flex h-full flex-col rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <HeartHandshake size={16} className="text-pink-500" />
          돌봄일지
        </h2>
      </header>
      <p className="text-sm leading-relaxed text-cb-gray-600">
        부모님의 하루를 매일 기록하고
        <br />
        시설과 함께 관리하세요.
      </p>
      <ul className="mt-3 space-y-1.5 text-xs text-cb-gray-500">
        <li className="flex items-center gap-1.5">
          <Heart size={10} className="text-pink-400" /> 면회·식사·투약 기록
        </li>
        <li className="flex items-center gap-1.5">
          <Heart size={10} className="text-pink-400" /> 건강 지표 추이 차트
        </li>
        <li className="flex items-center gap-1.5">
          <Heart size={10} className="text-pink-400" /> 시설 양방향 기록
        </li>
      </ul>
      <Link
        to={ROUTES.CARE_LOG}
        className="mt-auto flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 px-3 py-2.5 text-sm font-bold text-white hover:opacity-90"
      >
        시작하기 <ArrowRight size={14} />
      </Link>
    </section>
  )
}

function GovResourcesWidget() {
  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <ExternalLink size={14} className="text-cb-primary" />
          정부·공공기관
        </h2>
        <Link
          to={ROUTES.GUIDE_RESOURCES}
          className="flex items-center gap-0.5 text-xs font-medium text-cb-gray-500 hover:text-cb-primary"
        >
          전체 <ChevronRight size={12} />
        </Link>
      </header>
      <ul className="flex flex-col gap-1.5">
        {GOVERNMENT_RESOURCES.slice(0, 4).map((res) => (
          <li key={res.id}>
            <a
              href={res.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-cb-gray-50"
            >
              <span className="block h-1 w-1 rounded-full bg-cb-gray-300" />
              <span className="truncate text-sm text-cb-gray-700">{res.name}</span>
              <ExternalLink
                size={11}
                className="ml-auto flex-shrink-0 text-cb-gray-300"
              />
            </a>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ============================================================================
 * Mobile — stacked cards
 * ========================================================================== */

function MobilePortalHome() {
  const navigate = useNavigate()
  const hasMounted = useHasMounted()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)

  // GPS
  const [loc, setLoc] = useState<{ lat: number | null; lng: number | null; name: string | null }>(
    { lat: null, lng: null, name: null }
  )
  const gpsTried = useRef(false)
  useEffect(() => {
    if (gpsTried.current || !navigator.geolocation) return
    gpsTried.current = true
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const { latitude, longitude } = p.coords
        try {
          const r = await fetch(
            `/api/v1/location/reverse-geocode?latitude=${latitude}&longitude=${longitude}`
          )
          const j = await r.json()
          setLoc({
            lat: latitude,
            lng: longitude,
            name: j.success ? j.data.addressName : '내 위치',
          })
        } catch {
          setLoc({ lat: latitude, lng: longitude, name: '내 위치' })
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    )
  }, [])

  // 검색
  const [kw, setKw] = useState('')
  const [items, setItems] = useState<
    { id: number; name: string; facilityType: string; addressSido: string }[]
  >([])
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChange = (val: string) => {
    setKw(val)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (val.trim().length >= 2) {
      timerRef.current = setTimeout(async () => {
        try {
          const res = await apiClient.get('/facilities/autocomplete', {
            params: { query: val.trim() },
          })
          setItems(res.data.data ?? [])
          setOpen(true)
        } catch {
          setItems([])
        }
      }, 300)
    } else {
      setItems([])
      setOpen(false)
    }
  }
  const submit = () => {
    setOpen(false)
    navigate(kw.trim() ? `/facilities?keyword=${encodeURIComponent(kw.trim())}` : '/facilities')
  }

  // 근처 인기 시설 — 쿼리 키는 SSR 프리페치(entry-server.tsx)와 공유 (위치 없으면 null/null)
  const { data: nearbyFacilities, isLoading: isNearbyLoading } = useQuery({
    queryKey: mobilePopularQueryKey(loc.lat, loc.lng),
    queryFn: async () => {
      const params: Record<string, any> = { page: 0, size: 8 }
      if (loc.lat && loc.lng) {
        params.latitude = loc.lat
        params.longitude = loc.lng
        params.radiusKm = 5
      }
      const res = await apiClient.get('/facilities', { params })
      return res.data.data.content ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  const facilityScrollRef = useRef<HTMLDivElement>(null)
  const scrollFacilityList = useCallback((direction: 'left' | 'right') => {
    if (!facilityScrollRef.current) return
    facilityScrollRef.current.scrollBy({
      left: direction === 'right' ? 260 : -260,
      behavior: 'smooth',
    })
  }, [])

  const QUICK = [
    { icon: Sparkles, label: 'AI 추천', to: ROUTES.RECOMMEND, color: 'from-purple-500 to-violet-600' },
    { icon: ClipboardCheck, label: '자가진단', to: ROUTES.SELF_ASSESSMENT, color: 'from-emerald-500 to-teal-600' },
    { icon: Calculator, label: '요양비용', to: ROUTES.COST_SIMULATOR, color: 'from-amber-500 to-orange-600' },
    { icon: Search, label: '시설검색', to: ROUTES.FACILITIES, color: 'from-blue-500 to-indigo-600' },
  ]

  return (
    <div className="flex flex-col bg-cb-gray-50 pb-6">
      {/* Hero (compact) */}
      <div className="relative bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#4338ca] px-5 pb-12 pt-8">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        <div className="relative">
          {hasMounted && isAuthenticated && user && (
            <p className="mb-1 text-xs text-white/70">
              안녕하세요, <span className="font-semibold text-white">{user.name}</span>님
            </p>
          )}
          <h1 className="text-xl font-bold leading-tight text-white">
            요양원·요양시설 비교
          </h1>
          <p className="mt-1 text-xs text-white/60">
            32,580개 시설 · 평가등급 · AI 맞춤
          </p>

          <div className="relative z-20 mt-4 flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-cb-gray-400"
              />
              <input
                type="text"
                value={kw}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                onFocus={() => items.length > 0 && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                placeholder="시설 이름·지역명 검색"
                className="w-full rounded-xl bg-white/95 py-2.5 pl-9 pr-3 text-sm shadow-lg focus:outline-none"
              />
              {open && items.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-cb-gray-100 bg-white shadow-xl">
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onMouseDown={() => {
                        setOpen(false)
                        navigate(`/facilities/${item.id}`)
                      }}
                      className="flex w-full items-center gap-2 border-b border-cb-gray-50 px-3 py-2 text-left last:border-0 hover:bg-cb-primary-light"
                    >
                      <Building2 size={12} className="flex-shrink-0 text-cb-gray-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium text-cb-gray-900">
                          {item.name}
                        </p>
                        <p className="text-[10px] text-cb-gray-500">
                          {FACILITY_TYPE_LABELS[item.facilityType] ?? item.facilityType} ·{' '}
                          {item.addressSido}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={submit}
              className="flex-shrink-0 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-[#2563eb] shadow-lg"
            >
              검색
            </button>
          </div>
        </div>
      </div>

      {/* Quick action 4-grid (overlap) */}
      <div className="relative z-10 -mt-6 px-5">
        <div className="grid grid-cols-4 gap-2 rounded-2xl border border-cb-gray-100 bg-white p-3 shadow-sm">
          {QUICK.map((q) => (
            <button
              key={q.label}
              onClick={() => navigate(q.to)}
              className="flex flex-col items-center gap-1 py-1.5"
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${q.color}`}
              >
                <q.icon size={18} className="text-white" />
              </div>
              <span className="text-[11px] font-semibold text-cb-gray-700">
                {q.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 시설 유형 칩 */}
      <FacilityTypeChips />

      {/* main_card_slider — 시설 유형 칩 직후 (영구지침 §3.1, v5.1.3).
          모바일은 별도 slotKey — PC용 728x90 애드핏이 모바일 폭을 넘겨 가로 스크롤을 만들지 않도록 분리.
          (모바일용 광고를 켜려면 adfitSlots 에 main_card_slider_mobile 을 320 이하 크기로 매핑) */}
      <div className="mt-4 px-5">
        <PromotionSlot
          slotKey="main_card_slider_mobile"
          limit={1}
          layout="single"
          fallback={<MockPromotionFallback variant="hero" />}
        />
      </div>

      {/* ─────────────────────────────────────────────────────────────────
       * 핵심 콘텐츠 우선 (영구지침: home-page-portal-spec.md §3.1)
       * 4. 근처 시설 → 5. HotRanking
       *  → main_my_below (NewsHeadline 직전, v5.1.4)
       *  → 6. News → 7. Community → 8. TodayInsight
       * ───────────────────────────────────────────────────────────────── */}

      {/* 근처 인기 시설 */}
      <section className="mt-5 px-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-cb-gray-900">
              {loc.name ? `${loc.name} 근처 시설` : '인기 시설'}
            </h2>
            {loc.name && (
              <p className="mt-0.5 flex items-center gap-1 text-[11px] text-cb-gray-500">
                <MapPin size={10} className="text-cb-primary" />
                반경 5km 내
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {nearbyFacilities && nearbyFacilities.length > 2 && (
              <>
                <button
                  onClick={() => scrollFacilityList('left')}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm"
                  aria-label="이전"
                >
                  <ChevronLeft size={14} className="text-cb-gray-600" />
                </button>
                <button
                  onClick={() => scrollFacilityList('right')}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm"
                  aria-label="다음"
                >
                  <ChevronRight size={14} className="text-cb-gray-600" />
                </button>
              </>
            )}
            <Link to={ROUTES.FACILITIES} className="text-xs font-semibold text-cb-primary">
              전체
            </Link>
          </div>
        </div>
        <div
          ref={facilityScrollRef}
          className="flex gap-3 overflow-x-auto pb-3"
          style={{ scrollbarWidth: 'none' }}
        >
          {isNearbyLoading
            ? [1, 2, 3].map((i) => <FacilityCardSkeleton key={i} />)
            : (nearbyFacilities ?? []).map((f: any) => (
                <Link
                  key={f.id}
                  to={`/facilities/${f.id}`}
                  className="w-[210px] flex-shrink-0"
                >
                  <Card noPadding className="overflow-hidden">
                    {f.thumbnailUrl ? (
                      <img
                        src={f.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        className="h-24 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-24 w-full items-center justify-center bg-cb-gray-100">
                        <MapPin size={22} className="text-cb-gray-300" />
                      </div>
                    )}
                    <div className="flex flex-col gap-1.5 p-3">
                      <div className="flex items-center gap-1">
                        <Badge variant="type" className="text-[9px]">
                          {FACILITY_TYPE_LABELS[f.facilityType] ?? f.facilityType}
                        </Badge>
                        {f.evaluationGrade && (
                          <Badge
                            variant="grade"
                            grade={f.evaluationGrade}
                            className="text-[9px]"
                          />
                        )}
                      </div>
                      <h3 className="truncate text-sm font-bold text-cb-gray-900">
                        {f.name}
                      </h3>
                      <p className="flex items-center gap-1 text-[11px] text-cb-gray-500">
                        <MapPin size={10} className="text-cb-primary" />
                        <span className="truncate">
                          {f.address ?? f.addressSido}
                        </span>
                      </p>
                    </div>
                  </Card>
                </Link>
              ))}
        </div>
      </section>

      {/* HotRanking (NEW, mobile) */}
      <div className="mt-5 px-5">
        <HotRankingWidget />
      </div>

      {/* main_my_below — NewsHeadline 직전 (영구지침 §3.1 v5.1.4) */}
      <div className="mt-4 px-5">
        <PromotionSlot
          slotKey="main_my_below"
          limit={1}
          layout="single"
          fallback={<MockPromotionFallback variant="hero" />}
        />
      </div>

      {/* NewsHeadline (NEW, mobile) */}
      <div className="mt-4 px-5">
        <NewsHeadlineCard />
      </div>

      {/* 체크리스트 진입 CTA (mobile) — 광고/뉴스 다음으로 내려 연속 배너 인상 회피 */}
      <div className="mt-5 px-5">
        <ChecklistPromoCard variant="home" />
      </div>

      {/* 커뮤니티 인기글 */}
      <div className="mt-5 px-5">
        <CommunityHotPosts />
      </div>

      {/* TodayInsight — 콘텐츠 묶음 마무리 (큐레이션) */}
      <div className="mt-4 px-5">
        <TodayInsightCard />
      </div>

      {/* ─────────────────────────────────────────────────────────────────
       * 가입 유도 (비로그인만) + 광고 묶음
       * ───────────────────────────────────────────────────────────────── */}

      {/* MyWidget — 모바일은 비로그인 사용자에게만 노출
          (로그인 시 헤더·BottomNav 마이탭으로 동선 충분, 모바일 vertical space 절약) */}
      {hasMounted && !isAuthenticated && (
        <div className="mt-5 px-5">
          <MyWidget />
        </div>
      )}

      {/* 슬롯 main_side_ad_grid (2x2 자체 서비스 CTA 그리드) */}
      <div className="mt-4 px-5">
        <PromotionSlot
          slotKey="main_side_ad_grid"
          limit={4}
          layout="grid"
          fallback={<MockPromotionFallback variant="grid" count={4} />}
        />
      </div>

      {/* PopularFacilities (TOP 5 평점, mobile은 별도 섹션) */}
      <div className="mt-4 px-5">
        <PopularFacilities />
      </div>

      {/* 통계 */}
      <div className="mt-2 px-5">
        <StatisticsSection />
      </div>

      {/* 가이드 */}
      <div className="mt-2 px-5">
        <GuideWidget />
      </div>

      {/* 비용 시뮬레이터 */}
      <div className="mt-4 px-5">
        <CostSimulatorPromo />
      </div>

      {/* 돌봄일지 */}
      <div className="mt-4 px-5">
        <CareLogPromo />
      </div>

      {/* 슬롯 main_mobile_inline (정부·공공기관 직전 광고 모집) */}
      <div className="mt-4 px-5">
        <PromotionSlot
          slotKey="main_mobile_inline"
          fallback={<MockPromotionFallback variant="inline" />}
        />
      </div>

      {/* 정부기관 */}
      <div className="mt-4 px-5">
        <GovResourcesWidget />
      </div>

      {/* 블로그 */}
      <div className="mt-4 px-5">
        <BlogHeadlines />
      </div>

      {/* 쇼핑몰 */}
      <div className="mt-4 px-5">
        <ShopRecommend />
      </div>

      {/* 슬롯 main_hero — 모바일은 별도 slotKey.
          PC용 728x90 애드핏이 모바일 폭을 넘겨 가로 스크롤을 만들지 않도록 분리.
          (모바일 광고를 켜려면 adfitSlots 에 main_hero_mobile 을 320 이하 크기로 매핑) */}
      <div className="mt-4 px-5">
        <PromotionSlot
          slotKey="main_hero_mobile"
          fallback={<MockPromotionFallback variant="hero" />}
        />
      </div>

      {/* PWA 설치 카드 */}
      <div className="mt-4 px-5">
        <PwaInstallCard />
      </div>

      {/* CTA */}
      <section className="mt-6 px-5">
        <div className="rounded-2xl bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] px-5 py-6 text-center">
          <h3 className="text-base font-bold text-white">
            지금 우리 동네 시설을 찾아보세요
          </h3>
          <p className="mt-1 text-xs text-white/60">무료 상담 · AI 추천 · 자가진단</p>
          <button
            onClick={() => navigate(ROUTES.FACILITIES)}
            className="mt-4 rounded-xl bg-white px-6 py-2.5 text-sm font-bold text-[#2563eb] shadow-md"
          >
            시설 검색하기
          </button>
        </div>
      </section>
    </div>
  )
}

function FacilityCardSkeleton() {
  return (
    <div className="w-[210px] flex-shrink-0">
      <Card noPadding className="overflow-hidden">
        <Skeleton height="96px" className="rounded-none" />
        <div className="flex flex-col gap-1.5 p-3">
          <Skeleton height="0.875rem" width="75%" />
          <Skeleton height="0.7rem" width="50%" />
        </div>
      </Card>
    </div>
  )
}
